
import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PlusCircle, Folder } from "lucide-react";
import { ProjectList } from "@/components/ProjectList";
import { ProjectForm } from "@/components/ProjectForm";
import { Project } from "@/types/Project";
import { EmptyState } from "@/components/EmptyState";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface ProjectTabsProps {
  activeTab: string;
  setActiveTab: (value: string) => void;
  projects: Project[];
  isLoading: boolean;
  handleAddProject: (project: Project) => void;
  handleArchiveProject: (id: string) => void;
  handleSelectProject: (project: Project) => void;
  handleCreateNewProject: () => void;
}

export function ProjectTabs({
  activeTab,
  setActiveTab,
  projects,
  isLoading,
  handleAddProject,
  handleArchiveProject,
  handleSelectProject,
  handleCreateNewProject,
}: ProjectTabsProps) {
  const activeProjects = projects?.filter(p => !p.isArchived) || [];

  const handleProjectFormSuccess = (project: Project) => {
    handleAddProject(project);
    setActiveTab("list");
  };

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
      <TabsList className="grid w-full grid-cols-2 mb-2">
        <TabsTrigger value="list" className="relative">
          <span className="flex items-center gap-1">
            <Folder className="w-4 h-4" />
            Projects
          </span>
          {activeProjects.length > 0 && (
            <span className="ml-2 bg-primary/20 text-primary text-xs rounded-full px-2 py-0.5">
              {activeProjects.length}
            </span>
          )}
        </TabsTrigger>
        <TabsTrigger 
          value="new" 
          className="font-bold text-primary-foreground bg-primary hover:bg-primary/90 hover:text-primary-foreground transition-colors"
        >
          <PlusCircle className="w-4 h-4 mr-1" />
          New Project
        </TabsTrigger>
      </TabsList>
      
      <TabsContent value="list">
        {isLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="glass-card p-4 rounded-lg animate-pulse">
                <div className="h-5 bg-gray-200 rounded w-1/3 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/4"></div>
              </div>
            ))}
          </div>
        ) : activeProjects.length === 0 ? (
          <EmptyState
            title="No projects yet"
            description="Create your first project to start tracking time and invoicing clients."
            actionLabel="Create New Project"
            onAction={handleCreateNewProject}
            icon={<Folder className="h-12 w-12 opacity-20" />}
          />
        ) : (
          <ProjectList
            projects={activeProjects}
            onArchive={handleArchiveProject}
            onSelect={handleSelectProject}
          />
        )}
      </TabsContent>
      
      <TabsContent value="new">
        <div className="glass-card rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium">Create a New Project</h3>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="text-muted-foreground cursor-help">
                    <Info className="h-4 w-4" />
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>Projects help you organize your work and track time. You can set hourly rates and create invoices from your tracked time.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <ProjectForm onSuccess={handleProjectFormSuccess} />
        </div>
      </TabsContent>
    </Tabs>
  );
}
